import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';
import { popupHttps, popupLocalDate, miniRecord, validateMini } from './popupAdminFields';
import { objectStorageApi } from '../../../api/objectStorage';

export default function PopupDisplayManagement() {
  const user = useUserStore((state) => state.user);
  return <MiniSession key={JSON.stringify([user?.id, user?.role])} />;
}
function MiniSession() {
  const endpoint = '/admin/ads/startup-popup/mini';
  const [value, setValue] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const pending = useRef(false),
    life = useRef({ live: true, read: 0 });
  useEffect(() => {
    const session = life.current;
    session.live = true;
    return () => {
      session.live = false;
      session.read += 1;
    };
  }, []);
  const draft = useAdminDraftGuard(value, { enabled: value !== null || busy, busy });
  const load = async () => {
    if (pending.current || !draft.confirmDiscard()) return;
    const session = life.current,
      read = ++session.read;
    const current = () => session.live && read === session.read;
    setLoading(true);
    setError('');
    setSaved(false);
    try {
      const { data } = await api.getAuth(endpoint);
      if (!current()) return;
      const next = miniRecord(data.data);
      setValue(next);
      draft.markClean(next);
    } catch {
      if (current()) setError('설정을 불러오지 못했습니다.');
    } finally {
      if (current()) setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (key, next) => {
    if (pending.current) return;
    setValue((v) => ({ ...v, [key]: next }));
    setSaved(false);
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || loading || !value) return;
    pending.current = true;
    const session = life.current;
    session.read += 1;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const payload = {
        ...value,
        title: value.title.trim(),
        titleEn: value.titleEn.trim(),
        imageUrl: value.imageUrl.trim(),
        destinationUrl: value.destinationUrl.trim(),
      };
      validateMini(payload);
      const { data } = await api.putAuth(endpoint, payload);
      if (!session.live) return;
      const confirmed = miniRecord(data.data);
      setValue(confirmed);
      draft.markClean(confirmed);
      setSaved(true);
    } catch (e) {
      if (session.live) setError(e.response?.data?.msg || e.message || '저장하지 못했습니다.');
    } finally {
      pending.current = false;
      if (session.live) setBusy(false);
    }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (pending.current || loading || !file) return;
    if (
      !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
      file.size > 5 * 1024 * 1024
    ) {
      setError('PNG/JPG/WebP 5MB 이하를 선택하세요.');
      return;
    }
    pending.current = true;
    const session = life.current;
    session.read += 1;
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const body = new FormData();
      body.append('image', file);
      const result = await objectStorageApi.uploadImage(body);
      if (!session.live) return;
      if (!popupHttps(result.data?.url || '')) throw new Error();
      setValue((v) => ({ ...v, imageUrl: result.data.url }));
    } catch {
      if (session.live) setError('이미지를 업로드하지 못했습니다.');
    } finally {
      pending.current = false;
      if (session.live) setBusy(false);
    }
  };
  const fontSize =
    Number.isInteger(value?.fontSize) && value.fontSize >= 10 && value.fontSize <= 14
      ? value.fontSize
      : 14;
  return (
    <Box component="form" onSubmit={save} aria-label="미니 팝업 설정">
      <Stack
        spacing={2}
        component="fieldset"
        disabled={busy || loading}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0, overflowWrap: 'anywhere' }}
      >
        {error && (
          <Alert
            severity="error"
            action={
              !value ? (
                <Button disabled={busy || loading} onClick={load}>
                  재시도
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}
        {!value ? (
          loading ? (
            <Typography>설정을 불러오는 중…</Typography>
          ) : null
        ) : (
          <>
            <Typography variant="h6">미니 팝업 · 모든 탭</Typography>
            <Alert severity="info">
              하단 내비게이션이 있는 모든 탭에서 표시됩니다. 기본 높이 52pt이며 미니플레이어가
              있으면 그 위, 없으면 탭바 위에 12pt 간격으로 배치됩니다. 접속 광고·알림 안내·키보드와
              동시에 표시하지 않습니다. ×를 누르면 오늘은 숨깁니다.
            </Alert>
            <TextField
              required={value.enabled}
              label="문구"
              inputProps={{ maxLength: 100 }}
              value={value.title}
              onChange={(e) => change('title', e.target.value)}
              helperText="짧은 한두 문장을 권장합니다. 큰 글자에서는 높이가 늘어납니다."
            />
            <TextField
              label="영문 문구 (선택)"
              inputProps={{ maxLength: 100 }}
              value={value.titleEn}
              onChange={(e) => change('titleEn', e.target.value)}
            />
            <TextField
              required={value.enabled}
              label="랜딩 URL"
              type="url"
              value={value.destinationUrl}
              onChange={(e) => change('destinationUrl', e.target.value)}
            />
            <Button component="label" disabled={busy || loading} variant="outlined">
              선택 썸네일 업로드
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                aria-label="미니 팝업 썸네일 파일"
                onChange={upload}
              />
            </Button>
            {value.imageUrl && <Button onClick={() => change('imageUrl', '')}>썸네일 제거</Button>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="배너 배경 색상"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="color"
                value={value.backgroundColor}
                onChange={(e) => change('backgroundColor', e.target.value)}
              />
              <TextField
                label="문구 색상"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="color"
                value={value.textColor}
                onChange={(e) => change('textColor', e.target.value)}
              />
            </Stack>
            <Button
              variant="outlined"
              onClick={() => {
                change('backgroundColor', '#F16438');
                change('textColor', '#111111');
              }}
            >
              포스트팝 기본 색상으로
            </Button>
            <Typography variant="caption">
              기본: 포스트팝 주황 배경 · 검정 문구. 색상을 고른 뒤 아래 저장을 눌러 적용하세요.
            </Typography>
            <FormControlLabel
              label="AD 표시"
              control={
                <Checkbox
                  checked={value.showAdMark}
                  onChange={(e) => change('showAdMark', e.target.checked)}
                />
              }
            />
            {['startsAt', 'endsAt'].map((key) => (
              <TextField
                key={key}
                label={key === 'startsAt' ? '공개 시작 (선택)' : '공개 종료 (선택)'}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={popupLocalDate(value[key])}
                onChange={(e) => {
                  if (pending.current) return;
                  const next = e.target.value;
                  if (next && !Number.isFinite(Date.parse(next))) {
                    setError('유효한 날짜를 입력해주세요.');
                    return;
                  }
                  change(key, next ? new Date(next).toISOString() : null);
                }}
              />
            ))}
            <Box sx={{ containerType: 'inline-size', maxWidth: 360, minWidth: 0 }}>
              <Box
                aria-label="미니 팝업 미리보기"
                sx={{
                  maxWidth: 360,
                  position: 'relative',
                  minHeight: 52,
                  bgcolor: value.backgroundColor,
                  color: value.textColor,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: 0,
                  '@container (max-width: 220px)': {
                    display: 'grid',
                    gridTemplateColumns: '1fr 44px',
                    pb: 2,
                    '& .mini-preview-copy': { gridColumn: '1 / -1', gridRow: 2, pr: 1.5 },
                    '& .mini-preview-close': { gridColumn: 2, gridRow: 1 },
                  },
                  overflow: 'hidden',
                  pl: 1.5,
                  pr: 0,
                  py: 1,
                  gap: 1.25,
                }}
              >
                {value.imageUrl && (
                  <Box
                    component="img"
                    src={value.imageUrl}
                    alt=""
                    sx={{
                      width: 32,
                      height: 32,
                      flexShrink: 0,
                      objectFit: 'cover',
                      borderRadius: '6px',
                    }}
                  />
                )}
                <Box className="mini-preview-copy" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      overflowWrap: 'anywhere',
                      fontSize,
                      lineHeight: `${fontSize * 1.5}px`,
                      fontWeight: 500,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 2,
                      overflow: 'hidden',
                      color: `${value.textColor} !important`,
                    }}
                  >
                    {value.title || '미니 팝업 문구'}
                  </Typography>
                </Box>
                <Box
                  className="mini-preview-close"
                  aria-hidden
                  sx={{
                    width: 44,
                    flexShrink: 0,
                    minHeight: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                  }}
                >
                  ×
                </Box>
                {value.showAdMark && (
                  <Box
                    component="span"
                    sx={{
                      position: 'absolute',
                      right: 4,
                      bottom: 4,
                      fontSize: 6,
                      fontWeight: 500,
                      lineHeight: '8px',
                      color: `${value.textColor} !important`,
                      pointerEvents: 'none',
                    }}
                  >
                    광고
                  </Box>
                )}
              </Box>
            </Box>
            <FormControlLabel
              label="미니 팝업 공개"
              control={
                <Checkbox
                  checked={value.enabled}
                  onChange={(e) => change('enabled', e.target.checked)}
                />
              }
            />
            {saved && (
              <Alert severity="success">
                저장했습니다. 다음 앱 실행에서 적용됩니다. 오늘 숨긴 기기의 기록은 유지됩니다.
              </Alert>
            )}
            <Button type="submit" variant="contained">
              {busy ? '처리 중…' : '저장'}
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
